import Papa from 'papaparse';
import { token } from '@/infrastructure/container';

export interface CsvParseResult {
  headers: string[];
  rows: Record<string, string>[];
  headerRowIndex: number;
}

export interface ICsvParser {
  parse(file: File): Promise<CsvParseResult>;
  parseText(text: string): CsvParseResult;
}

export const TOKEN_ICsvParser = token<ICsvParser>('ICsvParser');

const isHeaderRow = (cells: string[]): boolean => {
  const nonEmpty = cells.filter((c) => c.trim().length > 0);
  if (nonEmpty.length < 2) return false;
  const allNumeric = nonEmpty.every((c) => /^-?\d+(\.\d+)?$/.test(c.trim()));
  return !allNumeric;
};

export class CsvParser implements ICsvParser {
  async parse(file: File): Promise<CsvParseResult> {
    const text = await file.text();
    return this.parseText(text);
  }

  parseText(text: string): CsvParseResult {
    const result = Papa.parse<string[]>(text, {
      skipEmptyLines: true,
      header: false,
    });
    const matrix = result.data as string[][];
    let headerRowIndex = 0;
    for (let i = 0; i < Math.min(matrix.length, 10); i++) {
      if (isHeaderRow(matrix[i])) {
        headerRowIndex = i;
        break;
      }
    }
    const headers = (matrix[headerRowIndex] ?? []).map((h) => h.trim());
    const rows: Record<string, string>[] = [];
    for (let i = headerRowIndex + 1; i < matrix.length; i++) {
      const row = matrix[i];
      if (!row || row.every((c) => c.trim().length === 0)) continue;
      const obj: Record<string, string> = {};
      for (let c = 0; c < headers.length; c++) {
        obj[headers[c]] = (row[c] ?? '').trim();
      }
      rows.push(obj);
    }
    return { headers, rows, headerRowIndex };
  }
}
